/**
 * Export one answer as a document the reader can keep.
 *
 * The CSV/Excel downloads take the result set; this takes the reply itself —
 * what the agent wrote — for pasting into a ticket, a review doc, or a mail
 * thread. Two things are deliberately left out:
 *
 *  - the result table: that is what the spreadsheet formats are for, and rows
 *    pasted into prose are unreadable anyway;
 *  - the query / API requests behind the answer: the export is the answer as
 *    something to hand to a reader, and the SQL is plumbing that reader never
 *    asked for (it also names tables and filters that have no business in a
 *    forwarded document).
 *
 * PDF is generated client-side and downloads directly, exactly like the
 * Markdown file — no print dialog, no popup. The composed Markdown is parsed
 * with remark (the same parser react-markdown uses for the live chat bubble)
 * into a real syntax tree, then walked into pdfmake's declarative content
 * model — pdfmake itself is dynamically imported so its weight is paid on
 * the first PDF click, not on page load.
 */

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import rehypeParse from 'rehype-parse';
import config from '../config';
import interRegularUrl from '../fonts/Inter-Regular.ttf';
import interBoldUrl from '../fonts/Inter-Bold.ttf';
import interItalicUrl from '../fonts/Inter-Italic.ttf';
import { sanitizeHTML } from './sanitize';
import { splitMentions } from './mentions';

/** Text out of the several shapes an answer body arrives in. */
export const answerText = (content) => {
  if (typeof content === 'string') return content;
  if (content == null || content === false) return '';
  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.query || JSON.stringify(item);
        return String(item);
      })
      .join('\n\n');
  }
  if (typeof content === 'object') return content.query || JSON.stringify(content, null, 2);
  return String(content);
};

/**
 * Drop the ##N$$ citation markers the agent embeds for the chat UI's inline
 * citation chips. An exported document has no reference panel for them to
 * point at, so they go entirely — the answer reads as clean prose.
 */
export const replaceCitationMarkers = (text) => text
  .replace(/ *##\d+\$+/g, '')
  .replace(/[ \t]+$/gm, '');

/**
 * Is this answer body the result set rather than something written?
 *
 * The same `message` field carries either, depending on the turn: a rag reply
 * puts prose there, while a sql reply puts the rows there — as a markdown table
 * or as an array of row objects.
 */
export const isResultSet = (content) => {
  if (Array.isArray(content)) return true;
  if (content && typeof content === 'object') return !content.query;
  if (typeof content !== 'string') return false;
  const text = content.trim();
  if (text.startsWith('|')) return true;
  // A multi-step SQL turn puts the data-source dump here — "### Step 1 - ..."
  // headings wrapping HTML tables — which is the result set under a heading, not
  // something written. Recognising only the markdown-pipe and JSON shapes let
  // that dump through as prose, so every table landed in the PDF the CSV and
  // Excel downloads exist to carry.
  if (/<table[\s>]/i.test(text)) return true;
  return text.startsWith('[') || text.startsWith('{');
};

/**
 * A timestamp a reader in another office can place: local time plus the zone
 * it was local to. Without the zone an exported document says 3:04 PM and
 * means nothing to whoever it was forwarded to.
 */
export const stampText = (date) => date.toLocaleString(undefined, { timeZoneName: 'short' });

/**
 * A display name is not Markdown, and it arrives from the IdP rather than from
 * us — `Options`/`MainContent` pass the OIDC profile name straight through. A
 * newline in it ends the italic stamp and turns the rest of the name into
 * blocks of the document; `*`, `_`, `[` and friends open emphasis or a link.
 * Both reach remark on the PDF path, which is why this sits at the Markdown
 * composition boundary and not inside `exportedLine` — the HTML export feeds
 * the same stamp through its own `escapeHtml`, where a backslash would show
 * up as a backslash.
 */
const markdownText = (value) => (value
  ? String(value).replace(/\s+/g, ' ').replace(/[\\`*_[\]<>#|~]/g, '\\$&')
  : value);

/**
 * The "who and when" stamp every export carries. One definition so the answer
 * exports and the session exports cannot drift into stamping differently.
 * `exportedBy` is emitted verbatim: each caller escapes for its own format.
 */
export const exportedLine = (exportedBy, at = new Date()) => {
  const who = exportedBy && String(exportedBy).trim() ? ` by ${String(exportedBy).trim()}` : '';
  return `Exported${who} · ${stampText(at)}`;
};

/**
 * Compose the Markdown document: the question, when it was asked, who exported
 * it and when. Nothing else — see the header comment for why the query and
 * requests stay out.
 *
 * @param {{question?: string, answer?: *, description?: string,
 *          askedAt?: Date, exportedBy?: string}} turn
 */
export const buildMarkdown = ({
  question,
  answer,
  description,
  askedAt,
  exportedBy,
} = {}) => {
  const parts = [];
  const written = replaceCitationMarkers(answerText(description)).trim();
  const body = replaceCitationMarkers(answerText(answer)).trim();

  parts.push(question ? `# ${markdownMentions(question.trim())}` : '# Answer');
  if (askedAt instanceof Date && !Number.isNaN(askedAt.getTime())) {
    parts.push(`_Asked ${stampText(askedAt)}_`);
  }
  parts.push(`_${exportedLine(markdownText(exportedBy))}_`);

  const bodyIsProse = !!body && !isResultSet(answer);

  if (written) parts.push(written);
  // Add the body only when it is prose the reader would want, and not a repeat
  // of what is already written above.
  if (bodyIsProse && body !== written) parts.push(body);
  if (!written && !bodyIsProse) parts.push('_This answer had no text._');

  return `${parts.join('\n\n')}\n`;
};

/**
 * Shared convention for the message list: author_id 0 (or the string "0") is
 * the agent's own turn; anything else — including a missing id on malformed
 * data — is the user's. Kept in one place so the export composer and the
 * live message list (MainContent.jsx) can't drift into disagreeing about it.
 */
export const isUserMessage = (authorId) => authorId !== 0 && authorId !== '0' && authorId !== null;

/**
 * Walk a session into segments tagged by what they are: the title, a real
 * conversation-turn question, or a stretch of written/body prose. Shared by
 * `buildSessionMarkdown` (which flattens this to one string for the .md
 * download, where "## question" is a perfectly good heading) and the PDF
 * path (which cannot afford that flattening — an agent's own answer often
 * contains its own "## " subheadings, e.g. structuring a long answer into
 * sections, and those must never be mistaken for a real asked question just
 * because they share a heading depth with one).
 *
 * @param {string} title    session summary, used as the document title
 * @param {Array}  messages the screen's own formatted message list — each
 *                          { author_id, message, description }; author_id 0
 *                          is the agent, anything else is the user.
 */
const sessionSegments = (title, messages = [], { exportedBy } = {}) => {
  const segments = [
    { type: 'title', text: (title || '').trim() || 'Discover chat' },
    { type: 'prose', text: `_${exportedLine(markdownText(exportedBy))}_` },
  ];

  // Mirrors ChatScreen.jsx's own visibleMessages filter: a bot message the
  // app synthesises at index 0 (the default "Hi! I'm your assistant..."
  // greeting) never appears in the live transcript, so the export shouldn't
  // show it either.
  const visibleMessages = messages[0]?.author_id === 0 && !messages[0]?.isError
    ? messages.slice(1)
    : messages;

  let pendingQuestion = null;
  let turns = 0;

  // A question that never got its own answer turn (e.g. a failed send that
  // was retried) still belongs in the transcript rather than vanishing under
  // the next question that happened to follow it.
  const flushUnanswered = () => {
    if (!pendingQuestion) return;
    segments.push({ type: 'question', text: pendingQuestion });
    segments.push({ type: 'prose', text: '_No answer yet._' });
    turns += 1;
    pendingQuestion = null;
  };

  visibleMessages.forEach((m) => {
    if (isUserMessage(m.author_id)) {
      const q = replaceCitationMarkers(answerText(m.message)).trim();
      if (q) {
        flushUnanswered();
        pendingQuestion = q;
      }
      return;
    }
    const written = replaceCitationMarkers(answerText(m.description)).trim();
    const body = replaceCitationMarkers(answerText(m.message)).trim();
    const bodyIsProse = !!body && !isResultSet(m.message);

    const answerParts = [];
    if (written) answerParts.push(written);
    if (bodyIsProse && body !== written) answerParts.push(body);
    if (!answerParts.length) {
      // The turn answered with a result table — the transcript says so rather
      // than silently dropping the question.
      answerParts.push('_This answer was a result table — use the data downloads on the message itself._');
    }

    if (pendingQuestion) {
      segments.push({ type: 'question', text: pendingQuestion });
      pendingQuestion = null;
      turns += 1;
    }
    answerParts.forEach((text) => segments.push({ type: 'prose', text }));
  });

  // A trailing question the agent never answered (e.g. still streaming when
  // exported) still belongs in the transcript.
  flushUnanswered();

  if (!turns) segments.push({ type: 'prose', text: '_This chat had no messages._' });
  return segments;
};

/**
 * Compose the whole session as one Markdown document, mirroring the chat's
 * own shape: the session title on top, then each question as a heading with
 * the written answer under it, in conversation order. Result tables and the
 * queries stay out for the same reasons as the single-answer export; a
 * table-only turn keeps its question with a note so the transcript still
 * reads complete.
 */
export const buildSessionMarkdown = (title, messages = [], { exportedBy } = {}) => {
  const parts = sessionSegments(title, messages, { exportedBy }).map((seg) => {
    if (seg.type === 'title') return `# ${seg.text}`;
    // Only the flattened Markdown gets the code spans. `sessionSegments` stays
    // clean so the PDF path, which parses the same segments, paints its own
    // chip rather than rendering the mention in Courier as well.
    if (seg.type === 'question') return `## ${markdownMentions(seg.text)}`;
    return seg.text;
  });
  return `${parts.join('\n\n')}\n`;
};

/**
 * Whether a turn has anything worth exporting as a document.
 *
 * Shares `isResultSet` with the composer so the menu never offers a file that
 * would come out saying the answer had no text, and strips citation markers
 * first so a description that is purely a `##N$$` marker — which the composer
 * reduces to nothing — doesn't pass this check either.
 */
export const hasExportableText = (turn) => {
  if (!turn) return false;
  if (typeof turn.description === 'string' && replaceCitationMarkers(turn.description).trim()) return true;
  if (isResultSet(turn.answer)) return false;
  return !!replaceCitationMarkers(answerText(turn.answer)).trim();
};

/** A filesystem-safe stem taken from the question. */
export const exportFilename = (question, extension) => {
  const stem = (question || 'discover-answer')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'discover-answer';
  return `${stem}.${extension}`;
};

// Palette mirrors the app: ink for prose, muted for meta, the brand purple for
// structure (cover band, Q badge, question bubble) so the document reads as
// Discover's, not as plain print.
const PAGE_MARGIN = 56;
// A4 width (595pt) minus both margins — every full-width canvas line/card
// uses this so they line up with each other and with the page edges.
const CONTENT_WIDTH = 483;
const INK = '#16181d';
const MUTED = '#5b6271';
const CODE_BG = '#f4f6fa';
const BRAND = '#7c6bff';
const BRAND_SOFT = '#efedfd';
const WHITE = '#ffffff';
const RULE = '#dbe0ea';
// The mention chip inside a question bubble. Mixed at 40% of BRAND over
// BRAND_SOFT (the bubble's own fill) rather than the app's 30%: pdfmake paints
// a flat background behind a run and cannot draw the inset ring that carries
// the chip's edge on screen, so the fill has to do that work alone. Contrast
// against the bubble is 1.56; ink on the chip stays 9.8.
const MENTION_BG = '#c1b9fe';
// The same chip on the cover band, whose fill is solid BRAND and whose title
// is white. Darkened rather than lightened: a lighter wash would drop white
// 17pt bold from 3.89 to 2.6-3.0 against its own chip, under the 3:1 bar for
// large text. 30% of ink over the band reads as a chip (1.59) and takes the
// title's contrast up to 6.19 instead of down.
const COVER_MENTION_BG = '#5d52bb';
const ROW_ALT = '#f9fafd';

/** Depth-first plain string out of an inline mdast/hast subtree — no styling. */
const plainText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.value || '';
  // A code span holds its text in `value` and has no children, so walking only
  // children silently dropped it — and the question headings this reads carry
  // their mentions as code spans.
  if (node.type === 'inlineCode') return node.value || '';
  if (node.children) return node.children.map(plainText).join('');
  return '';
};

/**
 * A file path, a hyphenated word like "dataset-handle", or a CamelCase
 * identifier has no spaces at all, so pdfmake's word-based wrapping treats
 * it as one unbreakable token — its full width becomes the table column's
 * minimum width, and inside a narrow column (several long identifier-heavy
 * columns competing for the same 483pt) that width can exceed what's left,
 * with the overflow silently clipped rather than wrapped. Zero-width spaces
 * after separators, and at CamelCase boundaries, give the wrapper places to
 * break; invisible, so they cost nothing when a break isn't needed.
 *
 * Only applied within already-whitespace-delimited tokens of some length —
 * short ones (a decimal like "170.0", a spaced-out "-") never risk overflow
 * on their own, and touching them would plant an invisible character in
 * ordinary prose for no reason. Applied to every long run, not just code
 * spans — plain prose ("dataset-handle" written without backticks) hits the
 * exact same wall.
 */
const ZERO_WIDTH_SPACE = String.fromCharCode(0x200b);
const breakable = (text) => String(text).replace(/\S{12,}/g, (word) => word
  .replace(/([/._-])/g, `$1${ZERO_WIDTH_SPACE}`)
  .replace(/([a-z0-9])([A-Z])/g, `$1${ZERO_WIDTH_SPACE}$2`));

/** mdast inline nodes -> a flat array of pdfmake text runs (bold/italic/link kept per-run). */
const mdastInline = (nodes = []) => nodes.flatMap((node) => {
  switch (node.type) {
    case 'text':
      return node.value ? [{ text: breakable(node.value) }] : [];
    case 'strong':
      return mdastInline(node.children).map((r) => ({ ...r, bold: true }));
    case 'emphasis':
      return mdastInline(node.children).map((r) => ({ ...r, italics: true }));
    case 'delete':
      return mdastInline(node.children).map((r) => ({ ...r, decoration: 'lineThrough' }));
    case 'inlineCode':
      return [{ text: breakable(node.value), font: 'Courier' }];
    case 'link':
      return mdastInline(node.children).map((r) => ({ ...r, link: node.url, color: BRAND, decoration: 'underline' }));
    case 'break':
      return [{ text: '\n' }];
    default:
      return node.children ? mdastInline(node.children) : [];
  }
});

/**
 * The same runs, with each @mention carrying the chip's background.
 *
 * `breakable()` has already planted zero-width spaces inside any long token,
 * which would split a long mention in the middle of the match, so each run is
 * stripped back to its plain text, divided, and re-broken per piece — the
 * wrap hints land in exactly the same places, since a mention and its
 * neighbour are separated by a space and never share a token.
 */
const ZERO_WIDTH_RE = new RegExp(ZERO_WIDTH_SPACE, 'g');
const withMentionChips = (runs, background = MENTION_BG) => runs.flatMap((run) => splitMentions(String(run.text ?? '').replace(ZERO_WIDTH_RE, ''))
  .map((part) => ({ ...run, text: breakable(part.text), ...(part.isMention ? { background } : {}) })));

/**
 * Mentions as code spans — the only chip Markdown has. It does change the
 * literal characters someone pastes out of the file, which is the cost of
 * marking a mention in a format that has no styling of its own.
 */
const markdownMentions = (text) => splitMentions(String(text ?? ''))
  .map((part) => (part.isMention ? `\`${part.text}\`` : part.text))
  .join('');

/** A short standalone string (e.g. a literal question) -> its inline runs, via a mini parse. */
const parseInlineOnly = (text) => {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(text);
  const runs = (tree.children || []).flatMap((n) => (n.type === 'paragraph' ? mdastInline(n.children) : []));
  return runs.length ? runs : [{ text }];
};

/** hast (parsed HTML) inline nodes -> the same flat pdfmake run shape as mdastInline. */
const hastInline = (nodes = []) => nodes.flatMap((node) => {
  if (node.type === 'text') return node.value ? [{ text: breakable(node.value) }] : [];
  if (node.type !== 'element') return [];
  const kids = hastInline(node.children);
  switch (node.tagName) {
    case 'strong': case 'b':
      return kids.map((r) => ({ ...r, bold: true }));
    case 'em': case 'i':
      return kids.map((r) => ({ ...r, italics: true }));
    case 'code':
      return kids.map((r) => ({ ...r, font: 'Courier' }));
    case 'br':
      return [{ text: '\n' }];
    case 'a':
      return kids.map((r) => ({ ...r, link: node.properties?.href, color: BRAND, decoration: 'underline' }));
    default:
      return kids;
  }
});

/** A borderless single-cell table used as a tinted panel (bubble/code background). */
const panel = (content, { fillColor, margin = [0, 0, 0, 0], contentMargin = [0, 0, 0, 0], unbreakable = false }) => ({
  table: { widths: ['*'], body: [[{ ...content, margin: contentMargin }]] },
  layout: { hLineWidth: () => 0, vLineWidth: () => 0, fillColor: () => fillColor },
  margin,
  ...(unbreakable ? { unbreakable: true } : {}),
});

// The badge column's width matches the circle's own diameter exactly — it
// used to be wider than the circle (26 vs 18), so the "Q"'s `alignment:
// center` centered against the wider column instead of the circle, landing
// visibly right of true center. columnGap supplies the gap that extra width
// used to give for free.
const BADGE_SIZE = 18;
const BUBBLE_GAP = 10;
const BUBBLE_PAD_X = 14;
const BUBBLE_PAD_Y = 10;
const BUBBLE_RADIUS = 10;
const BUBBLE_FONT_SIZE = 12;

// Set once per document build — the one place in this file that needs to
// know the body font ahead of layout, to measure how many lines a question
// will wrap to before it's drawn. See wrappedLineCount below for why.
let activeBodyFont = 'Helvetica';
const FONT_FAMILY_CSS = { Inter: 'Inter, sans-serif', Helvetica: 'Helvetica, Arial, sans-serif' };

/**
 * A rounded card (the question bubble's fill) needs its height before it's
 * drawn — unlike the auto-sizing table it replaces, a pdfmake canvas rect
 * can't size itself to its own text. Estimates the wrapped line count using
 * the browser's own text metrics for the same font pdfmake embeds, so the
 * estimate tracks the real font rather than a blind guess. Falls back to an
 * average-character-width estimate where no canvas 2D context is available
 * (e.g. this test environment) — errs toward overestimating on either path,
 * so the card comes out a touch tall rather than the text overflowing it.
 */
const wrappedLineCount = (runs, fontSize, maxWidth) => {
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const ctx = canvas ? canvas.getContext('2d') : null;
  const family = FONT_FAMILY_CSS[activeBodyFont] || FONT_FAMILY_CSS.Helvetica;
  const measure = ctx
    ? (word, bold, italics) => {
      ctx.font = `${italics ? 'italic ' : ''}${bold ? 'bold ' : ''}${fontSize}px ${family}`;
      return ctx.measureText(word).width;
    }
    : (word) => word.length * fontSize * 0.62;

  let lines = 1;
  let x = 0;
  runs.forEach((run) => {
    String(run.text).split(/(\s+)/).forEach((word) => {
      if (!word) return;
      const isSpace = /^\s+$/.test(word);
      const w = measure(word, run.bold, run.italics);
      if (x + w > maxWidth && x > 0) {
        lines += 1;
        x = 0;
        if (isSpace) return;
      }
      x += w;
    });
  });
  return lines;
};

/** The chat's question bubble, in print: gutter Q badge + rounded tinted card. */
const questionBubble = (runs) => {
  const boldRuns = runs.map((r) => ({ ...r, bold: true }));
  const plainQuestionText = runs.map((r) => r.text).join('');
  const cardWidth = CONTENT_WIDTH - BADGE_SIZE - BUBBLE_GAP;
  const textWidth = cardWidth - BUBBLE_PAD_X * 2;
  // A few points of safety margin on the estimate's own wrap width, so a
  // font-metrics mismatch between the browser's canvas and pdfmake's
  // embedded font biases toward an extra line rather than a cramped one.
  const lines = wrappedLineCount(boldRuns, BUBBLE_FONT_SIZE, textWidth - 6);
  const lineHeight = BUBBLE_FONT_SIZE * 1.35;
  const cardHeight = lines * lineHeight + BUBBLE_PAD_Y * 2;

  return {
    // Never split a question bubble across a page break — it's meant to read
    // as one small, self-contained card, not a fragment of one.
    unbreakable: true,
    columns: [
      {
        width: BADGE_SIZE,
        stack: [
          { canvas: [{ type: 'ellipse', x: BADGE_SIZE / 2, y: BADGE_SIZE / 2, r1: BADGE_SIZE / 2, r2: BADGE_SIZE / 2, color: BRAND }] },
          { text: 'Q', color: WHITE, bold: true, fontSize: 9, alignment: 'center', relativePosition: { x: 0, y: -13.5 } },
        ],
      },
      {
        width: cardWidth,
        stack: [
          { canvas: [{ type: 'rect', x: 0, y: 0, w: cardWidth, h: cardHeight, r: BUBBLE_RADIUS, color: BRAND_SOFT }] },
          // The horizontal padding is a spacer column plus a fixed text width,
          // not a margin: pdfmake ignores `margin` on a node positioned with
          // `relativePosition`, so the text sat flush against the card's left
          // edge and wrapped across its full width — one line wider than the
          // height estimate above allowed for.
          {
            columns: [
              { width: BUBBLE_PAD_X, text: '' },
              {
                width: textWidth,
                text: boldRuns,
                fontSize: BUBBLE_FONT_SIZE,
                outline: true,
                outlineText: plainQuestionText,
              },
            ],
            columnGap: 0,
            relativePosition: { x: 0, y: -(cardHeight - BUBBLE_PAD_Y) },
          },
        ],
      },
    ],
    columnGap: BUBBLE_GAP,
    margin: [0, 0, 0, 14],
  };
};

/** A markdown/HTML table -> pdfmake's table content, widths sized to content. */
const tableContent = (rows) => {
  if (!rows.length) return null;
  const cols = Math.max(...rows.map((r) => r.length));
  return {
    table: {
      headerRows: 1,
      // A row taller than the space left on a page otherwise gets split mid-
      // cell — a "(dataset-handle)" continuation line stranded at the top of
      // the next page with none of its own row's other cells beside it, no
      // border tying it back. Keeping each row whole moves the split point
      // to a row boundary instead.
      dontBreakRows: true,
      widths: new Array(cols).fill('auto'),
      body: rows.map((row, ri) => {
        const cells = row.slice(0, cols);
        while (cells.length < cols) cells.push({ text: '' });
        return cells.map((cell, ci) => ({
          ...cell,
          bold: ri === 0 || cell.bold,
          fillColor: ri === 0 ? BRAND_SOFT : (ri % 2 === 0 ? ROW_ALT : undefined),
          margin: [8, 8, 8, 8],
          alignment: cell.alignment,
        }));
      }),
    },
    layout: {
      hLineColor: () => RULE, vLineColor: () => RULE, hLineWidth: () => 0.5, vLineWidth: () => 0,
    },
    margin: [0, 8, 0, 22],
  };
};

// Question bubbles are never derived from a heading depth encountered while
// parsing prose — see sessionSegments' doc comment for why. Every heading
// found while walking mdast (including a "## " an agent wrote to structure
// its own answer) is just a heading, at a size stepping down from the title.
const HEADING_STYLE = { 1: 16, 2: 13.5, 3: 11.5, 4: 11, 5: 11, 6: 11 };
// Extra top space before each level, on top of what naturally separates
// blocks — a bigger gap before a bigger heading reads as "new section"
// rather than every heading looking like the same kind of break.
const HEADING_MARGIN_TOP = { 1: 24, 2: 18, 3: 12 };

/**
 * A heading at any depth (mdast or hast, markdown or HTML branch) -> the same
 * pdfmake content, so a "Key Insights" HTML card's own headings read at the
 * same visual weight as the surrounding markdown's. Depth 1 gets a rule under
 * it — a bigger gap alone reads as "some space", a rule reads as "new
 * section" — while flipping through a long multi-page document.
 */
const headingBlock = (runs, depth) => {
  const size = HEADING_STYLE[depth] || 11;
  const marginTop = HEADING_MARGIN_TOP[depth] || 10;
  const boldRuns = runs.map((r) => ({ ...r, bold: true }));
  // Depth 1/2 headings become PDF bookmarks (the reader's own viewer sidebar)
  // — real navigation for a multi-page document, absent until now. Depth 3+
  // stays out: that granular would turn the bookmark panel into noise.
  const outlineProps = depth <= 2 ? { outline: true, outlineText: runs.map((r) => r.text).join('') } : {};
  if (depth === 1) {
    return {
      stack: [
        { text: boldRuns, fontSize: size, margin: [0, 0, 0, 6], ...outlineProps },
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineColor: BRAND, lineWidth: 1.5 }] },
      ],
      margin: [0, marginTop, 0, 12],
    };
  }
  return { text: boldRuns, fontSize: size, margin: [0, marginTop, 0, depth === 2 ? 8 : 4], ...outlineProps };
};

/** mdast block nodes -> an array of pdfmake content nodes. */
const mdastBlock = (node) => {
  switch (node.type) {
    case 'heading':
      return [headingBlock(mdastInline(node.children), node.depth)];
    case 'paragraph': {
      const runs = mdastInline(node.children);
      if (!runs.length) return [];
      // A paragraph that is *entirely* `_italic_` is one of this file's own
      // meta notes ("No answer yet.", "This chat had no messages.") — muted,
      // like the app's own meta text, not full-ink prose.
      if (node.children.length === 1 && node.children[0].type === 'emphasis') {
        return [{ text: runs, italics: true, color: MUTED, fontSize: 9.5, margin: [0, 0, 0, 12] }];
      }
      return [{ text: runs, margin: [0, 0, 0, 12] }];
    }
    case 'list':
      return [{
        [node.ordered ? 'ol' : 'ul']: node.children.map(mdastListItem),
        markerColor: BRAND,
        margin: [0, 0, 0, 9],
      }];
    case 'table':
      return [tableContent(node.children.map(
        (row) => row.children.map((cell, colIdx) => ({ text: mdastInline(cell.children), alignment: mdastAlign(node, colIdx) })),
      ))].filter(Boolean);
    case 'code':
      return [panel(
        { text: node.value, font: 'Courier', fontSize: 9, color: INK },
        { fillColor: CODE_BG, margin: [0, 4, 0, 14], contentMargin: [8, 8, 8, 8] },
      )];
    case 'thematicBreak':
      return [{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineColor: RULE }], margin: [0, 8, 0, 14] }];
    case 'blockquote':
      return [{ stack: node.children.flatMap(mdastBlock), margin: [12, 4, 0, 12], color: MUTED, italics: true }];
    case 'html':
      return htmlBlock(parseHtmlFragment(node.value));
    default:
      return node.children ? node.children.flatMap(mdastBlock) : [];
  }
};

const mdastAlign = (tableNode, colIdx) => {
  const align = tableNode.align?.[colIdx];
  return align === 'center' || align === 'right' ? align : undefined;
};

/** One `listItem`'s children (a paragraph, maybe a nested list) -> one pdfmake list entry. */
const mdastListItem = (item) => {
  const parts = item.children.flatMap((child) => (
    child.type === 'paragraph' ? [{ text: mdastInline(child.children) }] : mdastBlock(child)
  ));
  if (!parts.length) return { text: '' };
  return parts.length === 1 ? parts[0] : { stack: parts };
};

/** Parse a fragment of raw HTML (already sanitized) into a hast node list. */
const parseHtmlFragment = (html) => {
  const tree = unified().use(rehypeParse, { fragment: true }).parse(sanitizeHTML(html));
  return tree.children || [];
};

const isBlockTag = (node) => node.type === 'element'
  && ['p', 'div', 'section', 'ul', 'ol', 'table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'hr'].includes(node.tagName);

/** hast block nodes (parsed HTML, e.g. a "Key Insights" card) -> pdfmake content nodes. */
const htmlBlock = (nodes = []) => nodes.flatMap((node) => {
  if (node.type === 'text') return node.value.trim() ? [{ text: node.value, margin: [0, 0, 0, 8] }] : [];
  if (node.type !== 'element') return [];
  const kids = () => node.children || [];
  switch (node.tagName) {
    case 'p':
      return [{ text: hastInline(kids()), margin: [0, 0, 0, 8] }];
    case 'h1': return [headingBlock(hastInline(kids()), 1)];
    case 'h2': return [headingBlock(hastInline(kids()), 2)];
    case 'h3': case 'h4': case 'h5': case 'h6':
      return [headingBlock(hastInline(kids()), Number(node.tagName[1]))];
    case 'ul':
      return [{ ul: kids().filter((c) => c.tagName === 'li').map(htmlListItem), markerColor: BRAND, margin: [0, 0, 0, 9] }];
    case 'ol':
      return [{ ol: kids().filter((c) => c.tagName === 'li').map(htmlListItem), markerColor: BRAND, margin: [0, 0, 0, 9] }];
    case 'table':
      return [tableContent(htmlTableRows(node))].filter(Boolean);
    case 'hr':
      return [{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineColor: RULE }], margin: [0, 8, 0, 14] }];
    case 'blockquote':
      return [{ stack: htmlBlock(kids()), margin: [12, 4, 0, 12], color: MUTED, italics: true }];
    // Structural wrappers with no own presentation — descend into their content.
    case 'div': case 'section': case 'span': case 'body': case 'html':
      return kids().some(isBlockTag) ? htmlBlock(kids()) : [{ text: hastInline(kids()), margin: [0, 0, 0, 8] }];
    default:
      return htmlBlock(kids());
  }
});

const htmlListItem = (li) => {
  const parts = htmlBlock(li.children || []);
  if (!parts.length) return { text: '' };
  return parts.length === 1 ? parts[0] : { stack: parts };
};

const htmlTableRows = (tableNode) => {
  const rows = [];
  const collect = (node) => (node.children || []).forEach((c) => {
    if (c.tagName === 'tr') rows.push(c);
    else if (['thead', 'tbody', 'tfoot'].includes(c.tagName)) collect(c);
  });
  collect(tableNode);
  return rows.map((tr) => (tr.children || [])
    .filter((c) => c.tagName === 'td' || c.tagName === 'th')
    .map((cell) => ({ text: hastInline(cell.children), bold: cell.tagName === 'th' })));
};

/**
 * The brand an export is stamped with: the same white-label values the app's
 * top bar shows (REACT_APP_BRAND_NAME / _TAGLINE / _LOGO_URL, via runtime
 * config), so a client deployment's documents carry the client's mark. The
 * builders take the brand as a plain argument and stay synchronous; the
 * download helpers resolve it — including turning the logo URL into a data
 * URL, which is the only form pdfmake can embed — before building.
 */
export const DEFAULT_BRAND = { name: 'Discover', tagline: 'by elastiq.ai' };

const brandFromConfig = () => ({
  name: config.brandName,
  tagline: config.brandTagline,
  logoUrl: config.brandLogoUrl,
});

const brandOrDefault = (brand = {}) => ({
  name: (brand.name && String(brand.name).trim()) || DEFAULT_BRAND.name,
  tagline: brand.tagline == null ? DEFAULT_BRAND.tagline : String(brand.tagline).trim(),
  logoDataUrl: brand.logoDataUrl || null,
});

/**
 * A logo as a data URL, or null. Only PNG and JPEG are worth fetching — pdfmake
 * embeds nothing else — and any failure (offline, 404, a non-image response, a
 * blocked cross-origin read) leaves the band with the wordmark alone rather
 * than failing the export. A successful fetch is cached per URL — one fetch
 * per session, not one per click — while a null is never cached, so a logo
 * that was briefly unreachable comes back on the next export.
 */
const logoDataUrlCache = new Map();
export const fetchImageDataUrl = async (url) => {
  if (!url || typeof fetch !== 'function') return null;
  if (!logoDataUrlCache.has(url)) {
    logoDataUrlCache.set(url, (async () => {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      if (!/^image\/(png|jpe?g)$/i.test(blob.type)) return null;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
    })().catch(() => null).then((dataUrl) => {
      if (!dataUrl) logoDataUrlCache.delete(url); // only a real logo is worth keeping
      return dataUrl;
    }));
  }
  return logoDataUrlCache.get(url);
};

/** The configured brand with its logo fetched, ready for a builder. */
export const resolveBrand = async (brand = brandFromConfig()) => ({
  ...brandOrDefault(brand),
  logoDataUrl: brand.logoDataUrl || await fetchImageDataUrl(brand.logoUrl),
});

/** The chat's full-bleed-in-spirit cover band: brand color, logo + wordmark, title. */
const coverBand = (title, brand) => {
  const b = brandOrDefault(brand);
  const wordmark = {
    text: [
      { text: b.name, bold: true },
      ...(b.tagline ? [{ text: `  ${b.tagline}`, bold: false }] : []),
    ],
    color: WHITE,
    fontSize: 9,
  };
  const mark = b.logoDataUrl
    ? {
      columns: [
        { image: b.logoDataUrl, fit: [96, 22], width: 'auto' },
        { ...wordmark, margin: [0, 6, 0, 0] },
      ],
      columnGap: 10,
    }
    : wordmark;
  return panel(
    {
      stack: [
        mark,
        { text: withMentionChips([{ text: title }], COVER_MENTION_BG), color: WHITE, bold: true, fontSize: 17, margin: [0, 8, 0, 0] },
      ],
    },
    { fillColor: BRAND, margin: [0, 0, 0, 24], contentMargin: [18, 16, 18, 16], unbreakable: true },
  );
};

/** The title/page-number strip repeated on every page. */
const footerContent = (currentPage, pageCount, pageSize, title) => ({
  margin: [PAGE_MARGIN, 10, PAGE_MARGIN, 0],
  stack: [
    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: pageSize.width - PAGE_MARGIN * 2, y2: 0, lineColor: RULE }] },
    {
      columns: [
        { text: title, fontSize: 8, color: MUTED },
        { text: `Page ${currentPage} of ${pageCount}`, fontSize: 8, color: MUTED, alignment: 'right' },
      ],
      margin: [0, 6, 0, 0],
    },
  ],
});

/**
 * Compose the whole Markdown document into a pdfmake docDefinition — content
 * tree, brand cover band, and a numbered footer. Parsing goes through remark
 * (the same parser react-markdown uses for the live chat bubble) plus
 * remark-gfm for tables, so nested/mixed lists and tables glued directly
 * under a heading parse exactly as they do on screen. A block of raw HTML
 * (e.g. an agent-formatted "Key Insights" card) is recognised by remark as
 * its own node and handed to a parallel HTML-to-content walker instead of
 * printing as literal tags.
 *
 * Exported (rather than folded into `downloadPdf`) so its output — a plain
 * content tree — can be asserted on directly in tests, without a jsPDF/canvas
 * stand-in.
 */
export const buildPdfDocDefinition = (markdown, { title = 'Answer', bodyFont = 'Helvetica', brand } = {}) => {
  activeBodyFont = bodyFont;
  const b = brandOrDefault(brand);
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown);
  let children = tree.children || [];

  let coverTitle = title || 'Answer';
  if (children[0]?.type === 'heading' && children[0].depth === 1) {
    coverTitle = plainText(children[0]) || coverTitle;
    children = children.slice(1);
  }

  const content = [coverBand(coverTitle, b), ...children.flatMap(mdastBlock)];

  return {
    pageSize: 'A4',
    pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, 64],
    defaultStyle: { font: bodyFont, fontSize: 11, color: INK, lineHeight: 1.3 },
    footer: (currentPage, pageCount, pageSize) => footerContent(currentPage, pageCount, pageSize, coverTitle),
    content,
    info: { title: coverTitle, author: b.name, subject: `${b.name} answer export` },
  };
};

/**
 * Compose a whole session into a pdfmake docDefinition. Unlike
 * `buildPdfDocDefinition`, this never re-derives "is this a real question"
 * from a flat string's heading depth — it walks `sessionSegments`, so a
 * question bubble is used only for a segment this file itself tagged
 * `question` (an actual conversation turn), while every heading inside a
 * turn's own written answer — including one at the same "## " depth —
 * renders as a plain heading, via the same per-segment remark parse
 * `buildPdfDocDefinition` uses for the whole document.
 */
export const buildSessionPdfDocDefinition = (title, messages = [], { bodyFont = 'Helvetica', brand, exportedBy } = {}) => {
  activeBodyFont = bodyFont;
  const b = brandOrDefault(brand);
  const segments = sessionSegments(title, messages, { exportedBy });
  const coverTitle = segments[0].text;

  const content = [coverBand(coverTitle, b)];
  segments.slice(1).forEach((seg) => {
    if (seg.type === 'question') {
      content.push(questionBubble(withMentionChips(parseInlineOnly(seg.text))));
      return;
    }
    const tree = unified().use(remarkParse).use(remarkGfm).parse(seg.text);
    content.push(...(tree.children || []).flatMap(mdastBlock));
  });

  return {
    pageSize: 'A4',
    pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, 64],
    defaultStyle: { font: bodyFont, fontSize: 11, color: INK, lineHeight: 1.3 },
    footer: (currentPage, pageCount, pageSize) => footerContent(currentPage, pageCount, pageSize, coverTitle),
    content,
    info: { title: coverTitle, author: b.name, subject: `${b.name} chat session export` },
  };
};

// Inter is the application's typeface; the PDF embeds it so the document
// looks like the product, not like a generic fallback. Fetched once from our
// own bundled assets and cached as base64 for every later export in the
// session.
const FONT_FILES = [
  { url: interRegularUrl, vfs: 'Inter-Regular.ttf', style: 'normal' },
  { url: interBoldUrl, vfs: 'Inter-Bold.ttf', style: 'bold' },
  { url: interItalicUrl, vfs: 'Inter-Italic.ttf', style: 'italic' },
];

let interFontsPromise = null;
const fetchInterFonts = () => {
  if (!interFontsPromise) {
    interFontsPromise = Promise.all(
      FONT_FILES.map(async (f) => {
        const res = await fetch(f.url);
        if (!res.ok) throw new Error(`font fetch failed: ${res.status}`);
        const buf = await res.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buf);
        const CHUNK = 0x8000; // avoid call-stack limits on fromCharCode
        for (let i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
        }
        return { ...f, base64: btoa(binary) };
      }),
    ).catch((err) => {
      // Next export retries rather than being stuck with a rejected promise.
      interFontsPromise = null;
      throw err;
    });
  }
  return interFontsPromise;
};

/**
 * pdfmake (plus the standard-14 Helvetica/Courier font tables) is imported on
 * demand so its weight is paid on the first PDF click, never on page load —
 * the old print-dialog flow existed to avoid exactly that cost; code-splitting
 * removes the tradeoff. The app's own typeface is embedded too; a failed font
 * fetch degrades to the standard Helvetica rather than failing the export.
 */
const loadPdfMake = async () => {
  // The browser-bundle standard-font modules (not `pdfmake/standard-fonts/*`,
  // which assume Node's real filesystem for the AFM metrics) embed their own
  // AFM data as a virtual file system entry, via addFontContainer.
  const [{ default: pdfMake }, { default: helveticaFonts }, { default: courierFonts }] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/standard-fonts/Helvetica'),
    import('pdfmake/build/standard-fonts/Courier'),
  ]);
  pdfMake.addFontContainer(helveticaFonts);
  pdfMake.addFontContainer(courierFonts);

  let bodyFont = 'Helvetica';
  try {
    const fonts = await fetchInterFonts();
    const vfs = {};
    const faces = {};
    fonts.forEach((f) => { vfs[f.vfs] = f.base64; faces[f.style] = f.vfs; });
    pdfMake.addVirtualFileSystem(vfs);
    pdfMake.addFonts({ Inter: { normal: faces.normal, bold: faces.bold, italics: faces.italic, bolditalics: faces.bold } });
    bodyFont = 'Inter';
  } catch {
    // Helvetica fallback already registered above.
  }
  return { pdfMake, bodyFont };
};

/** Compose and directly download the PDF — same gesture as the Markdown file. */
export const downloadPdf = async (markdown, title, filename, { brand } = {}) => {
  const [{ pdfMake, bodyFont }, resolvedBrand] = await Promise.all([loadPdfMake(), resolveBrand(brand)]);
  const docDefinition = buildPdfDocDefinition(markdown, { title, bodyFont, brand: resolvedBrand });
  await pdfMake.createPdf(docDefinition).download(filename);
};

/** Compose and directly download the whole session as one PDF. */
export const downloadSessionPdf = async (title, messages, filename, { brand, exportedBy } = {}) => {
  const [{ pdfMake, bodyFont }, resolvedBrand] = await Promise.all([loadPdfMake(), resolveBrand(brand)]);
  const docDefinition = buildSessionPdfDocDefinition(title, messages, { bodyFont, brand: resolvedBrand, exportedBy });
  await pdfMake.createPdf(docDefinition).download(filename);
};

/** Save `text` as a file. Returns false when the browser blocks it. */
export const downloadTextFile = (text, filename, mimeType = 'text/markdown;charset=utf-8') => {
  try {
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked on the next frame: revoking synchronously can cancel the download
    // in some browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch {
    return false;
  }
};
